import React from 'react';
import FaBan from 'react-icons/lib/fa/ban';
import { defaultConfig } from '../services/API';

import ScreenerOnlyIcon from '../images/icons/screener_only_updated.png';
import UniversalIcon from '../images/icons/universal_updated.png';
import ORFOnlyIcon from '../images/icons/orf_only_updated.png';
import numbersRanIcon from '../images/icons/number_ran_icon.png';
import colorsRanIcon from '../images/icons/color_ran_icon.png';
import shapesRanIcon from '../images/icons/shape_ran_icon.png';
import ComprehensionIcon from '../images/icons/Comprehension.png';
import DecodingIcon from '../images/icons/Decoding.png';
import DyslexiaScreenerIcon from '../images/icons/DyslexiaScreener.png';
import SightIcon from '../images/icons/Sight.png';
import PhonAwareIcon from '../images/icons/PhonAware.png';

const ASSESSMENT_TASK = {
  RAN: 'RAN',
  PHONEME_SEGMENTATION: 'Phoneme Segmentation',
  PHONOLOGICAL_DELETION: 'Phonological Deletion/Elision',
  PHONEME_SUBSTITUTION: 'Phoneme Substitution',
  PHONOLOGICAL_WORKING_MEMORY: 'Phonological Working Memory',
  BLENDING: 'Blending (Legacy ISIP PA)',
  LETTER_NAMING: 'Letter Naming',
  LETTER_SOUNDS: 'Letter Sounds',
  LISTENING_COMPREHENSION_RETELL: 'Listening Comprehension (Retell)',
  SPELLING: 'Spelling',
  WORD_DECODING: 'Word Decoding',
  NONSENSE_WORD_FLUENCY: 'Nonsense Word Fluency',
  RECEPTIVE_VOCABULARY: 'Receptive Vocabulary',
  ORAL_READING_FLUENCY: 'Oral Reading Fluency',
  READING_COMPREHENSION: 'Reading Comprehension',
  LISTENING_COMPREHENSION_MC: 'Listening Comprehension (MC)',
  VISUAL_ATTENTION: 'Visual Attention',
  LETTER_NAMING_AND_SOUNDS: 'Letter Naming and Sounds',
};

const DYSLEXIA_TASKS = [
  { name: "RAN", value: "ran" },
  { name: "Word Manipulation", value: "wordManipulation" },
  { name: "Nonsense Word Repetition", value: "nonsenseWordReplication" },
  { name: "Letter Sounds", value: "letterSounds" },
  { name: "Word Reading", value: "wordReading" },
  { name: "Blending", value: "blending" }
];

export const READING_ROPE_DOMAIN = {
  DYSLEXIA_RISK: {
    name: 'Dyslexia Risk',
    icon: DyslexiaScreenerIcon,
  },
  DECODING: {
    name: 'Decoding',
    icon: DecodingIcon,
  },
  PHONEMIC_AWARENESS: {
    name: 'Phonemic Awareness',
    icon: PhonAwareIcon,
  },
  COMPREHENSION: {
    name: 'Comprehension',
    icon: ComprehensionIcon,
  },
  SIGHT_RECOGNITION: {
    name: 'High Frequency Words',
    icon: SightIcon,
  },
};

// Default assessment data
const CONFIGURATION_DATA = [
  {
    name: ASSESSMENT_TASK.RAN,
    description: 'Rapid automatized naming requires the student to identify and articulate the elements of a long list. Processing speed and accuracy are measured.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DYSLEXIA_RISK
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' }, // Pre-pk
    ],
    licenseTableName: "ranConfig"
  },
  {
    name: ASSESSMENT_TASK.PHONEME_SEGMENTATION,
    description: 'Students break words into individual phonemes, demonstrating their ability to identify and manipulate the smallest units of sound in spoken language.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.PHONEMIC_AWARENESS
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "upAndDownConfig"
  },
  {
    name: ASSESSMENT_TASK.PHONOLOGICAL_DELETION,
    description: 'Students remove specific phonemes from words and say the resulting word, testing their ability to manipulate sounds in spoken language.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.PHONEMIC_AWARENESS
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "wordPartDeletionConfig"
  },
  {
    name: ASSESSMENT_TASK.PHONEME_SUBSTITUTION,
    description: 'Students replace specific phonemes in words with new phonemes, demonstrating their ability to manipulate sounds in spoken language.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.PHONEMIC_AWARENESS
    ],
    configuration: [
      { value: true, ui: 'Required' }, 
      { value: true, ui: 'Required' }, 
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "changeOnePartConfig"
  },
  {
    name: ASSESSMENT_TASK.PHONOLOGICAL_WORKING_MEMORY,
    description: 'Students repeat nonsense words of increasing length, testing their ability to hold and manipulate sounds in working memory.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.PHONEMIC_AWARENESS
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "nonwordRepetitionConfig"
  },
  {
    name: ASSESSMENT_TASK.BLENDING,
    description: 'Students blend individual phonemes into complete words, demonstrating their ability to combine sounds into meaningful units.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.PHONEMIC_AWARENESS
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "blendingConfig"
  },
  {
    name: ASSESSMENT_TASK.LETTER_NAMING,
    description: 'Students identify letters by name, demonstrating their knowledge of letter names.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "letterNameConfig"
  },
  {
    name: ASSESSMENT_TASK.LETTER_SOUNDS,
    description: 'Students produce the sounds associated with letters, demonstrating their knowledge of letter-sound correspondences.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' }, // Pre-K
    ],
    licenseTableName: "letterSoundConfig"
  },
  {
    name: ASSESSMENT_TASK.LISTENING_COMPREHENSION_RETELL,
    description: 'Students retell a story they have heard, demonstrating their listening comprehension through oral recall.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.COMPREHENSION
    ],
    configuration: defaultConfig.LISTENING_COMP_CONFIG,
    licenseTableName: "retellConfig"
  },
  {
    name: ASSESSMENT_TASK.SPELLING,
    description: 'Students spell words, demonstrating their ability to encode spoken language into written form.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: defaultConfig.SPELLING_CONFIG,
    licenseTableName: "spellingConfig"
  },
  {
    name: ASSESSMENT_TASK.WORD_DECODING,
    description: 'Students read words, demonstrating their ability to decode written language.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: [
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' }, // Pre-k
    ],
    licenseTableName: "wifConfig"
  },
  {
    name: ASSESSMENT_TASK.NONSENSE_WORD_FLUENCY,
    description: 'Students read nonsense words, demonstrating their ability to apply decoding skills to unfamiliar words.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: defaultConfig.NWF_CONFIG,
    licenseTableName: "nwfConfig"
  },
  {
    name: ASSESSMENT_TASK.RECEPTIVE_VOCABULARY,
    description: 'Students match words to their meanings, demonstrating their understanding of vocabulary.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.COMPREHENSION
    ],
    configuration: defaultConfig.VOCABULARY_SCREENER_CONFIG,
    licenseTableName: "vocabularyConfig"
  },
  {
    name: ASSESSMENT_TASK.ORAL_READING_FLUENCY,
    description: 'Students read aloud, demonstrating their reading fluency and accuracy.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.DECODING
    ],
    configuration: [
      { value: false, ui: <FaBan size="30" color="#ccc" /> },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: true, ui: 'Required' },
      { value: false, ui: <FaBan size="30" color="#ccc" /> }, // Pre-K
    ],
    licenseTableName: "orfConfig"
  },
  {
    name: ASSESSMENT_TASK.READING_COMPREHENSION,
    description: 'Students answer questions about what they have read, demonstrating their reading comprehension.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.COMPREHENSION
    ],
    configuration: defaultConfig.READING_COMP_CONFIG,
    licenseTableName: "readingCompConfig"
  },
  {
    name: ASSESSMENT_TASK.LISTENING_COMPREHENSION_MC,
    description: 'Students answer multiple choice questions about what they have heard, demonstrating their listening comprehension skills in Spanish.',
    reading_rope_domain: [
      READING_ROPE_DOMAIN.COMPREHENSION
    ],
    configuration: defaultConfig.LISTENING_COMP_MC_CONFIG,
    licenseTableName: "listeningCompConfig",
    spanishOnly: true
  }
];

// Filter options
const ASSESSMENT_MODE_OPTIONS = [
  {
    name: 'UNIVERSAL SCREENER',
    icon: UniversalIcon,
    description: 'Students complete a series of short reading tasks followed by an Oral Reading Fluency passage.',
    filter: [
      ASSESSMENT_TASK.RAN,
      ASSESSMENT_TASK.WORD_MANIPULATION,
      ASSESSMENT_TASK.NONSENSE_WORD_REPETITION,
      ASSESSMENT_TASK.LETTER_SOUNDS,
      ASSESSMENT_TASK.WORD_READING,
      ASSESSMENT_TASK.BLENDING,
      ASSESSMENT_TASK.NWF,
      ASSESSMENT_TASK.SPELLING,
      ASSESSMENT_TASK.VOCABULARY,
      ASSESSMENT_TASK.LISTENING_COMPREHENSION,
      ASSESSMENT_TASK.ORAL_READING_FLUENCY,
      ASSESSMENT_TASK.READING_COMPREHENSION,
      ASSESSMENT_TASK.LISTENING_COMPREHENSION_MC
    ],
    assessmentEnabled: true,
    screenerEnabled: true
  },
  {
    name: 'ORF ONLY',
    icon: ORFOnlyIcon,
    description: 'Students read an Oral Reading Fluency passage aloud.',
    filter: [
      ASSESSMENT_TASK.ORAL_READING_FLUENCY,
    ],
    assessmentEnabled: true,
    screenerEnabled: false
  },
  {
    name: 'DYSLEXIA SCREENER ONLY',
    icon: ScreenerOnlyIcon,
    description: 'Students complete a series of short reading tasks. No Oral Reading Fluency passage will be administered.',
    filter: [
      ASSESSMENT_TASK.RAN,
      ASSESSMENT_TASK.WORD_MANIPULATION,
      ASSESSMENT_TASK.NONSENSE_WORD_REPETITION,
      ASSESSMENT_TASK.LETTER_SOUNDS,
      ASSESSMENT_TASK.WORD_READING,
      ASSESSMENT_TASK.BLENDING,
      ASSESSMENT_TASK.NWF,
      ASSESSMENT_TASK.SPELLING,
      ASSESSMENT_TASK.VOCABULARY,
      ASSESSMENT_TASK.LISTENING_COMPREHENSION,
    ],
    assessmentEnabled: false,
    screenerEnabled: true
  }
];

const RAN_OPTIONS = [
  {
    name: 'Number RAN',
    icon: numbersRanIcon,
    description: 'Students read a series of numbers',
    licenseTableForms: [null, null, null, null, null, null, null],
    tag: 'NUMBER_RAN'
  },
  {
    name: 'Color RAN',
    icon: colorsRanIcon,
    description: 'Students read a series of colors',
    licenseTableForms: ['45a64e20-19a1-11ee-81fb-cf2b4b2a7ed5', 'a260e700-19a2-11ee-81fb-cf2b4b2a7ed5', null, null, null, null, null],
    tag: 'COLOR_RAN'
  },
  {
    name: 'Shape RAN',
    icon: shapesRanIcon,
    description: 'Students read a series of shape names',
    licenseTableForms: ['c75883c0-19a1-11ee-81fb-cf2b4b2a7ed5', '8234f890-19a2-11ee-81fb-cf2b4b2a7ed5', null, null, null, null, null],
    tag: 'SHAPE_RAN'
  }
]

const RAN_LABELS = {
  NUMBER_RAN: 'Number RAN',
  COLOR_RAN: 'Color RAN',
  SHAPE_RAN: 'Shape RAN'
}

const SPANISH_MAPPING = {
  BOTH_ONE_SESSION: {test: 1, sequence: ['esp','eng'], licenseTableName: "BOTH_ONE_SESSION"},
  BOTH_TWO_SESSIONS: {test: 2, sequence: ['esp','eng'], licenseTableName: "BOTH_TWO_SESSIONS"},
  BOTH_ONE_SESSION_ENGLISH_FIRST: {test: 1, sequence: ['eng','esp'], licenseTableName: "BOTH_ONE_SESSION_ENGLISH_FIRST"},
  BOTH_TWO_SESSIONS_ENGLISH_FIRST: {test: 2, sequence: ['eng','esp'], licenseTableName: "BOTH_TWO_SESSIONS_ENGLISH_FIRST"},
  ENGLISH_ONLY: {test: 1, sequence: ['eng'], licenseTableName: "ENGLISH_ONLY"},
  SPANISH_ONLY: {test: 1, sequence: ['esp'], licenseTableName: "SPANISH_ONLY"},
};

export {
  ASSESSMENT_TASK,
  DYSLEXIA_TASKS,
  ASSESSMENT_MODE_OPTIONS,
  CONFIGURATION_DATA,
  SPANISH_MAPPING,
  RAN_OPTIONS,
  RAN_LABELS
};